// Boot-time ports for the moved billing settings tree. These members are the
// app inputs that non-settings callers (global modals, upgrade flows, loaders)
// reach too, so they must be wired at app boot — before any lazy settings
// screen or modal renders — via `src/utils/commerce-billing-ui-wiring.ts`.
import {
  type AnchorHTMLAttributes,
  type ComponentType,
  type ForwardRefExoticComponent,
  type ReactNode,
  type RefAttributes,
  type SVGProps,
} from 'react';
import type { MessageDescriptor } from 'react-intl';

import type { components, Region } from '@heidi/schema/ml-scribe';
import {
  type QueryUserQueryResult,
  type QueryUserResponse,
  type QueryUserSelector,
} from '@heidi/workspace/data';
import type {
  AddOnProductView,
  BillingSeatType,
  CatalogueProductView,
  Period,
  ProductView,
  SubscriptionTier,
} from '../../../contracts';
import type { TrackTeamTrialUpgradeRequestSubmit } from '../../../contracts/team-trial-upgrade-request-analytics.types';
import type { CreditAllowance } from '../../../contracts/types/credit-allowance.types';
import type { ClinicianTrialRecapTestimonialVariant } from '../../trials-and-upgrades/clinician-trial-recap-testimonial/message';
import type { TeamTrialUpgradeRequestPlan } from '../../trials-and-upgrades/team-trial-upgrade-request-modal/atoms';

type PriceView = NonNullable<ProductView['prices']>[number];

/** Tracking event as emitted by the moved billing screens. */
export interface BillingTrackEvent {
  event_name: string;
  props?: Record<string, unknown>;
  options?: Record<string, unknown>;
}

export interface BillingUrls {
  getUsageLimitsHelpUrl: (locale?: string) => string;
  getBillingHelpCentreUrl: (locale?: string) => string;
  getBillingPlansPricingUrl: (locale?: string) => string;
  BILLING_PLANS_PRICING_URL: string;
  getGetInTouchUrl: (locale?: string) => string;
  GET_IN_TOUCH_URL: string;
  getComparePlansUrl: (locale?: string) => string;
  FREE_VS_PRO_PLANS_ACCOUNT_ANCHOR_HELP_ARTICLE_PATH: string;
}

export interface PaywallStates {
  toggleUpgradeProModal: (isOpen: boolean) => void;
  toggleTrialToPaidModal: (isOpen: boolean) => void;
  toggleClinicianUpgradeModal: (isOpen: boolean) => void;
  toggleClinicianMaxUpgradeModal: (isOpen: boolean) => void;
  togglePracticeUpgradeModal: (isOpen: boolean) => void;
}

/** `@/hooks/query/useQueryProducts` — the delegate, flag gates included. */
export interface ProductsPort {
  useQueryProducts: (queryParams?: Record<string, unknown>) => {
    catalogueVersion?: string;
    isError: boolean;
    data?: CatalogueProductView[];
    isPending: boolean;
    isLoading: boolean;
    isFetched: boolean;
  };
  formatPriceWithCurrency: (
    price: string,
    currencyCode: string,
    currencySymbol: string
  ) => string;
  getCurrencySymbol: (currencyCode: string) => string;
  getDisplayedPriceFromPlan: (
    plan?: PriceView,
    planDuration?: 'month' | 'year',
    showDuration?: boolean,
    locale?: string,
    durationText?: { monthShort?: string; yearShort?: string }
  ) => string | undefined;
  getNewProductTierProduct: (
    tier: SubscriptionTier,
    products?: CatalogueProductView[],
    seatType?: BillingSeatType
  ) => CatalogueProductView | undefined;
  getPlanFromPeriod: (
    period: Period,
    products?: ProductView | AddOnProductView
  ) => PriceView | undefined;
  getProductByName: (
    products: ProductView[] | undefined,
    name: string
  ) => ProductView | undefined;
  getProductUpgradeDisplayPrices: (
    product: ProductView | undefined,
    locale?: string
  ) => {
    monthlyPrice: string | undefined;
    annualPrice: string | undefined;
    annualSavePercent: number | undefined;
  };
  getTierProduct: (
    tier: SubscriptionTier | undefined,
    products?: ProductView[]
  ) => ProductView | undefined;
}

export interface BillingFeature {
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  title?: ReactNode;
  description?: string;
  notAvailableInRestrictedMarkets?: boolean;
  excludedRegions?: string[];
}

export interface EvidenceBillingInfoResult {
  evidenceBilling?:
    | components['schemas']['domains__billing__api__billing_schema__EvidenceBillingInfoResponseView']
    | null;
  evidenceBillingState?: components['schemas']['EvidenceBillingStateResponseView'] | null;
  isInFreeTrial: boolean;
  trialDaysRemaining: number;
  hasEvidencePro: boolean;
  hasEvidencePractice: boolean;
  freeTrialAvailable: boolean;
  hasTrialExpired: boolean;
  isTrialCancelledByUser: boolean;
  [key: string]: unknown;
}

export interface NinetyDayTrialVariantResult {
  showNinetyDayTrialVariant?: boolean;
  isNinetyDayTrialEnded?: boolean;
  isUpgradeDisabledForTeamMember?: boolean;
  shouldDisableButtonForNonStripeManagedTeam?: boolean;
  showNonAdminRequestBanner?: boolean;
  showContactSalesInsteadOfUpgrade?: boolean;
  planDisplayName?: string;
  handleContactSalesClick: () => void;
  hasCallbackRequested: boolean;
  [key: string]: unknown;
}

export interface UserQuotaResult {
  quota?: Record<string, unknown> | null;
  quotaResetDate?: string;
  inSessionQuotaRemaining?: number | null;
  customNoteQuotaRemaining?: number | null;
  askAiQuotaRemaining?: number | null;
  unlimitedQuotaUntil?: string | null;
  [key: string]: unknown;
}

/** `@/components/common/external-link` props (structural copy). */
export interface BillingExternalLinkProps extends Omit<
  AnchorHTMLAttributes<HTMLAnchorElement>,
  'href' | 'target' | 'rel'
> {
  target?: string;
  href: string;
  rel?: string;
  disabled?: boolean;
}

/** `Feature` from `@/components/modal/heidi-upgrade-base-modal` (structural copy). */
export interface BillingUpgradeModalFeature {
  id?: string;
  icon: ComponentType<{ size?: number | string; className?: string }>;
  description?: string;
}

/** `HeidiUpgradeBaseModalProps` (structural copy). */
export interface BillingHeidiUpgradeBaseModalProps {
  rightPanelContent?: ReactNode;
  features?: Array<BillingUpgradeModalFeature>;
  yearlySaveBadgeText?: string;
  title?: ReactNode;
  yearlyContent: ReactNode;
  monthlyContent?: ReactNode;
  primaryUpgradeButtonContent?: ReactNode;
  secondaryButton?: ReactNode;
  onUpgradeClick?: (period: Period) => void;
  isUpgradePending?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  footerText?: ReactNode;
  featuresTitle?: string;
  hideTestimonial?: boolean;
  variant?: 'pro' | 'practice';
  dismissible?: boolean;
  portalContainer?: HTMLElement | null;
  yearlyOnly?: boolean;
  showPeriodTabs?: boolean;
  isUpgradeDisabled?: boolean;
  upgradeDisabledTooltip?: string;
  hidePricing?: boolean;
  featuresContent?: ReactNode;
  footerCaptionClassName?: string;
  pricingFeaturesGroupedCard?: boolean;
  primaryCtaReplacement?: (
    assignPrimaryButtonRef: (el: HTMLElement | null) => void
  ) => ReactNode;
  primaryButtonTestId?: string;
}

/** `TeamTrialUpgradeRequestButtonProps` (structural copy). */
export type BillingTeamTrialUpgradeRequestButtonProps = {
  plan: TeamTrialUpgradeRequestPlan;
  requestToUpgradeMessage?: MessageDescriptor;
  requestedTooltipMessage?: MessageDescriptor;
} & (
  | {
      trackRequestSubmitted: TrackTeamTrialUpgradeRequestSubmit;
      onOpenTeamRequestModal?: never;
    }
  | {
      onOpenTeamRequestModal: () => void;
      trackRequestSubmitted?: never;
    }
);

export interface CommerceBillingUiPorts {
  // ── app components ──────────────────────────────────────────────────
  /** `@/components/apple-iap-info-text` — Apple-compliant billing copy. */
  AppleIapInfoText: ComponentType<{ className?: string }>;
  /** `@/components/common/external-link`. */
  ExternalLink: ComponentType<BillingExternalLinkProps>;
  /** `@/components/modal/heidi-upgrade-base-modal`. */
  HeidiUpgradeBaseModal: ComponentType<BillingHeidiUpgradeBaseModalProps>;
  /** `@/components/modal/heidi-upgrade-base-modal` price row. */
  PriceDisplay: ComponentType<{
    period: Period;
    product: ProductView;
    billingText?: string;
  }>;
  /** `@/features/trialsAndUpgrades/ninety-day-end-of-trial` feature list. */
  UpgradeTrialFeatureList: ComponentType<{
    tier: SubscriptionTier | undefined;
    testId?: string;
  }>;
  /** `@/features/trialsAndUpgrades/team-trial-upgrade-request-button`. */
  TeamTrialUpgradeRequestButton: ForwardRefExoticComponent<
    BillingTeamTrialUpgradeRequestButtonProps & RefAttributes<HTMLButtonElement>
  >;

  // ── atoms (value / setter pairs) ────────────────────────────────────
  useTrialExtensionModalOpenValue: () => boolean;
  useSetTrialExtensionModalOpen: () => (open: boolean) => void;
  usePracticeUpgradeModalState: () => [
    boolean,
    (open: boolean | ((prev: boolean) => boolean)) => void,
  ];
  useUpgradeModalFromTrialRecapState: () => [
    boolean,
    (v: boolean | ((prev: boolean) => boolean)) => void,
  ];
  useTrialModalPreviewDaysLeftValue: () => number | null;
  useSetTrialModalPreviewDaysLeft: () => (days: number | null) => void;
  useSetClinicianMaxUpgradeModalEntryPoint: () => (entryPoint: string | null) => void;
  useSetClinicianUpgradeModalOpen: () => (open: boolean) => void;
  /** `useSetAtom(practiceUpgradeModalOpenAtom)`. */
  useSetPracticeUpgradeModalOpen: () => (open: boolean) => void;
  /** `@/atoms/paywallAtoms` aggregate toggles. */
  usePaywallStates: () => PaywallStates;

  // ── flags (call-time thunks — never resolved booleans) ──────────────
  isBillingCustomDowngradeFlowsEnabled: () => boolean;
  isEndOf90DayTrialExperienceEnabled: () => boolean;
  isEvidenceLaunchTrialAndUpgradeFlowsEnabled: () => boolean;
  isEvidenceLaunchBillingPageRedesignEnabled: () => boolean;
  useIsHeidi2PricingPlansModalEnabled: () => boolean;
  useIsHeidi2TeamUpgradesEnabled: () => boolean;
  useIsHeidi2PracticeMaxUpgradeEnabled: () => boolean;
  useIsHeidi2OptInEnabled: () => boolean;
  useIsHeidi2FullUpgradeFlowEnabled: () => boolean;
  useIsEvidenceQueryLimitsForLegacyTiersEnabled: () => boolean;
  useIsWorkspaceSettingsIaEnabled: () => boolean;
  /** `@/components/layout/Navigation` gamify entry flag. */
  useIsGamifyTrialExtensionEntryEnabled: () => boolean;
  /** `@/features/post-note-quality` gamify arm flag. */
  useIsGamifyTrialArmAllowed: () => boolean;

  // ── legal / urls ────────────────────────────────────────────────────
  /** `@/components/utils/legalDocuments` usage policy url. */
  USAGE_POLICY: string;
  billingUrls: BillingUrls;
  getTermsOfUseLink: (locale: string) => string;

  // ── app hooks ───────────────────────────────────────────────────────
  useMutateApplySubscriptionDiscount: (args?: {
    onApplied?: () => void;
    onSuccess?: () => void;
  }) => {
    mutate: (vars: unknown) => void;
    isPending: boolean;
  };
  useMutateCheckoutV2: () => {
    mutate: (vars: unknown, options?: unknown) => void;
    mutateAsync: (vars: unknown) => Promise<unknown>;
    isPending: boolean;
  };
  useQueryCreditAllowance: (enabled?: boolean) => {
    data?: CreditAllowance;
    isPending?: boolean;
  };
  useQueryGamifyTrialExtension: (enabled?: boolean) => {
    data?: Record<string, unknown> | null;
  };
  products: ProductsPort;
  useQueryStripeLink: (enabled?: boolean) => {
    data?: { billing_portal_url?: string } | null;
    refetch: () => Promise<{ data?: { billing_portal_url?: string } | null }>;
  };
  /** `@/hooks/query/useQueryUser`. */
  useQueryUser: <T = QueryUserResponse>(
    select?: QueryUserSelector<T>
  ) => QueryUserQueryResult<T>;
  useEvidenceBillingInfo: () => EvidenceBillingInfoResult;
  useFeatureLists: (options?: {
    currentPaidTier?: SubscriptionTier | null | undefined;
  }) => { currentTierFeatures: BillingFeature[] } & Record<string, BillingFeature[]>;
  useShouldHideMarketRestrictedFeatures: () => {
    shouldHide: boolean;
    isPending: boolean;
  };
  useUpgradeModalTrialText: (args: {
    trialDaysLeft: number;
    showExpiredTitle: boolean;
    titleWhenExpiredKey: string;
    plan?: string;
    everythingInPlanPlusKey?: string;
    currentPlanName?: string;
  }) => {
    titleText: string;
    loseAccessText?: string;
    featuresTitle?: string;
    isShortTrial: boolean;
  };
  getCurrentPlanNameForTrial: (
    isReverseTrial: boolean,
    previousStatusBeforeTrial: string | null | undefined
  ) => string;
  useNinetyDayTrialVariant: (args: {
    isOpen: boolean;
    excludeFromVariant?: boolean;
    trackContactSalesClick: (input: { label: string }) => void;
    planNameMessage?: unknown;
    onClose: () => void;
  }) => NinetyDayTrialVariantResult;
  useUserQuota: () => UserQuotaResult;
  useRegion: () => Region;
  resolveClinicianTrialRecapTestimonialVariant: (
    region: Region,
    country: string | null | undefined
  ) => ClinicianTrialRecapTestimonialVariant;
  getPlanChangesHelpArticleUrl: (locale: string) => string;

  // ── analytics emits ─────────────────────────────────────────────────
  /** `@/lib/analytics/track` — `trackingEventName` is bound to the enum. */
  track: (event: BillingTrackEvent) => void;
  trackingEventName: {
    readonly ButtonClicked: string;
    readonly CancelFlowOpened: string;
    readonly CancelOfferAccepted: string;
    readonly CancelOfferDeclined: string;
    readonly CancelOfferShown: string;
    readonly CancelReasonSelected: string;
    readonly ModalViewed: string;
    readonly PaywallViewed: string;
    readonly ScreenViewed: string;
  };
  /** `@/models/analytics/features/payments` PaywallSource enum values. */
  paywallSource: { readonly PracticeUpgradeModal: string };
  /** `@/hooks/amplitude`. */
  useManualExposure: (args: Record<string, unknown>) => unknown;
  trackPracticeUpgradeModalContactSalesClick: (input: { label: string }) => void;
  trackSettingsBillingTeamUpgradeRequestSubmit: TrackTeamTrialUpgradeRequestSubmit;
  trackUpgradeToPracticeModalTeamUpgradeRequestSubmit: TrackTeamTrialUpgradeRequestSubmit;

  // ── environment ─────────────────────────────────────────────────────
  /** `@/utils/appConfiguration` — stays a call-time thunk. */
  isAppleCompliantBuild: () => boolean;
  /** `@/utils/buildProfile` const. */
  isOnPrem: boolean;
  /** `@/tauri/utils`. */
  isTauri: () => boolean;
  /** `@/tauri/utils`. */
  openUrlInTauri: (url: string) => void;

  // ── navigation ────────────────────────────────────────────────────
  /** `next/router` `useRouter`, narrowed to the legs the moved tree reads. */
  useAppRouter: () => {
    push: (href: string) => Promise<boolean>;
    replace: (href: string) => Promise<boolean>;
    isReady: boolean;
    asPath: string;
  };

  // ── misc app fns ────────────────────────────────────────────────────
  /** `@/lib/utils` sleep. */
  sleep: (ms: number) => Promise<unknown>;
  /** `@/utils/scribeHTTP`. */
  getStripeCallbackRedirect: (showReverseModal?: boolean) => string;
  getStripeReturnRedirect: () => string;
}

let ports: CommerceBillingUiPorts | null = null;

export const configureCommerceBillingUi = (next: CommerceBillingUiPorts): void => {
  ports = next;
};

/** Non-hook accessor for module-scope and utility callsites. */
export const getCommerceBillingUiPorts = (): CommerceBillingUiPorts => {
  if (!ports) {
    throw new Error(
      '[commerce/ui/settings/billing] not configured — call configureCommerceBillingUi() before rendering billing UI.'
    );
  }
  return ports;
};

export const useCommerceBillingUiPorts = (): CommerceBillingUiPorts =>
  getCommerceBillingUiPorts();

/** Test-only: drops the registered ports so a suite can assert the failure. */
export const __resetCommerceBillingUiPortsForTests = (): void => {
  ports = null;
};
